import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, Search, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  aiTrainingAPI,
  type TrainingItemResponse,
} from '@/services/aiTraining';
import { Pagination } from '@/components/shared/Pagination';

interface TrainingItem {
  id: string;
  title: string;
  category: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

// Helper function to map API response to component format
const mapTrainingItem = (item: TrainingItemResponse): TrainingItem => ({
  id: item.id,
  title: item.title,
  category: item.category.name,
  description: item.content,
  createdAt: new Date(item.createdAt),
  updatedAt: new Date(item.updatedAt),
});

export function AITraining() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<TrainingItem | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    customCategory: '',
    customCategoryDescription: '',
  });

  // Fetch categories from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => aiTrainingAPI.getTrainingCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get category names from fetched categories
  const categories = useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData.map((cat) => cat.name).sort();
  }, [categoriesData]);

  // Create category name to ID mapping from fetched categories
  const categoryMap = useMemo(() => {
    if (!categoriesData) return new Map<string, string>();
    const map = new Map<string, string>();
    categoriesData.forEach((cat) => {
      if (cat.name && cat.id) {
        map.set(cat.name, cat.id);
      }
    });
    return map;
  }, [categoriesData]);

  // Get category ID from selected category name
  const selectedCategoryId = useMemo(() => {
    if (selectedCategory === 'all' || !selectedCategory) return undefined;
    return categoryMap.get(selectedCategory);
  }, [selectedCategory, categoryMap]);

  // Fetch training data from API
  const {
    data: trainingResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'trainingInformation',
      {
        search: searchQuery,
        categoryId: selectedCategoryId,
        page: currentPage,
      },
    ],
    queryFn: () =>
      aiTrainingAPI.getTrainingInformation({
        page: currentPage,
        limit: 20,
        search: searchQuery || undefined,
        categoryId: selectedCategoryId,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Reset to page 1 when search or category changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Map API response to component format (already filtered by API)
  const trainingData: TrainingItem[] = useMemo(() => {
    if (!trainingResponse?.data) return [];
    return trainingResponse.data.map(mapTrainingItem);
  }, [trainingResponse]);

  const handleOpenAddDialog = () => {
    setFormData({
      title: '',
      category: '',
      description: '',
      customCategory: '',
      customCategoryDescription: '',
    });
    setIsAddDialogOpen(true);
  };

  const handleOpenEditDialog = (item: TrainingItem) => {
    setCurrentItem(item);
    setFormData({
      title: item.title,
      category: item.category,
      description: item.description,
      customCategory: '',
      customCategoryDescription: '',
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (item: TrainingItem) => {
    setCurrentItem(item);
    setIsDeleteDialogOpen(true);
  };

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: aiTrainingAPI.createTrainingInformation,
    onSuccess: () => {
      toast.success('Training information added successfully');
      queryClient.invalidateQueries({ queryKey: ['trainingInformation'] });
      setIsAddDialogOpen(false);
      setFormData({
        title: '',
        category: '',
        description: '',
        customCategory: '',
        customCategoryDescription: '',
      });
    },
    onError: (error: Error) => {
      toast.error(
        error.message || 'Failed to add training information. Please try again.'
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { content: string; categoryId: string; title: string };
    }) => aiTrainingAPI.updateTrainingInformation(id, data),
    onSuccess: () => {
      toast.success('Training information updated successfully');
      queryClient.invalidateQueries({ queryKey: ['trainingInformation'] });
      setIsEditDialogOpen(false);
      setCurrentItem(null);
      setFormData({
        title: '',
        category: '',
        description: '',
        customCategory: '',
        customCategoryDescription: '',
      });
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          'Failed to update training information. Please try again.'
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => aiTrainingAPI.deleteTrainingInformation(id),
    onSuccess: () => {
      toast.success('Training information deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['trainingInformation'] });
      setIsDeleteDialogOpen(false);
      setCurrentItem(null);
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          'Failed to delete training information. Please try again.'
      );
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: aiTrainingAPI.createTrainingCategory,
    onSuccess: (newCategory) => {
      // Invalidate categories to refetch the list
      queryClient.invalidateQueries({ queryKey: ['trainingCategories'] });
      return newCategory;
    },
    onError: (error: Error) => {
      toast.error(
        error.message || 'Failed to create category. Please try again.'
      );
      throw error;
    },
  });

  const handleAdd = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    let finalCategory = formData.category;
    let categoryId: string | undefined;

    // If custom category is selected, create it first
    if (formData.category === 'custom') {
      if (!formData.customCategory.trim()) {
        toast.error('Please enter a custom category name');
        return;
      }

      try {
        // Create the custom category first
        const newCategory = await createCategoryMutation.mutateAsync({
          name: formData.customCategory.trim(),
          description: formData.customCategoryDescription.trim() || '',
        });
        categoryId = newCategory.id;
        finalCategory = newCategory.name;
      } catch {
        // Error is already handled in mutation
        return;
      }
    } else {
      if (!finalCategory) {
        toast.error('Please select or enter a category');
        return;
      }

      // Find category ID from category name
      categoryId = categoryMap.get(finalCategory);

      if (!categoryId) {
        toast.error(
          'Category not found. Please select an existing category or create a new one.'
        );
        return;
      }
    }

    // Create training information with the category ID
    createMutation.mutate({
      title: formData.title.trim(),
      content: formData.description.trim(),
      categoryId: categoryId,
    });
  };

  const handleEdit = async () => {
    if (
      !currentItem ||
      !formData.title.trim() ||
      !formData.description.trim()
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    let finalCategory = formData.category;
    let categoryId: string | undefined;

    // If custom category is selected, create it first
    if (formData.category === 'custom') {
      if (!formData.customCategory.trim()) {
        toast.error('Please enter a custom category name');
        return;
      }

      try {
        // Create the custom category first
        const newCategory = await createCategoryMutation.mutateAsync({
          name: formData.customCategory.trim(),
          description: formData.customCategoryDescription.trim() || '',
        });
        categoryId = newCategory.id;
        finalCategory = newCategory.name;
      } catch {
        // Error is already handled in mutation
        return;
      }
    } else {
      if (!finalCategory) {
        toast.error('Please select or enter a category');
        return;
      }

      // Find category ID from category name
      categoryId = categoryMap.get(finalCategory);

      if (!categoryId) {
        toast.error(
          'Category not found. Please select an existing category or create a new one.'
        );
        return;
      }
    }

    // Update training information with the category ID
    updateMutation.mutate({
      id: currentItem.id,
      data: {
        title: formData.title.trim(),
        content: formData.description.trim(),
        categoryId: categoryId,
      },
    });
  };

  const handleDelete = () => {
    if (!currentItem) return;

    deleteMutation.mutate(currentItem.id);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'General FAQ': 'bg-blue-100 text-blue-800',
      'Product Information': 'bg-purple-100 text-purple-800',
      'Store Information': 'bg-green-100 text-green-800',
      'Business Hours': 'bg-orange-100 text-orange-800',
      'Shipping & Delivery': 'bg-cyan-100 text-cyan-800',
      'Returns & Exchanges': 'bg-pink-100 text-pink-800',
      'Care & Maintenance': 'bg-yellow-100 text-yellow-800',
      'Company History': 'bg-indigo-100 text-indigo-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            AI Training
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage FAQ and business information for AI training
          </p>
        </div>
        <Button onClick={handleOpenAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Information
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Search and filter training information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by title or description..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category Filter</Label>
              <Select
                value={selectedCategory}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Total Items</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-9 w-20" />
            ) : (
              <p className="text-3xl">{trainingData.length}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingCategories ? (
              <Skeleton className="h-9 w-20" />
            ) : (
              <p className="text-3xl">{categories.length}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Filtered Results</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-9 w-20" />
            ) : (
              <p className="text-3xl">{trainingData.length}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Training Data Display */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12 space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Error loading training information:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      ) : trainingData.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              No training information found. Add your first item to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {trainingData.map((item) => (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={getCategoryColor(item.category)}
                      variant="secondary"
                    >
                      {item.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value={item.id} className="border-0">
                      <AccordionTrigger className="py-0">
                        <span className="text-left font-medium">
                          {item.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <div className="space-y-4">
                          <p className="text-muted-foreground whitespace-pre-wrap">
                            {item.description}
                          </p>
                          <div className="flex items-center justify-between pt-4 border-t">
                            <div className="text-xs text-muted-foreground">
                              <p>
                                Created: {item.createdAt.toLocaleDateString()}
                              </p>
                              <p>
                                Updated: {item.updatedAt.toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenEditDialog(item)}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenDeleteDialog(item)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>
          {trainingResponse?.totalPages && trainingResponse.totalPages > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={trainingResponse.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}

      {/* Add Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          if (!createMutation.isPending && !createCategoryMutation.isPending) {
            setIsAddDialogOpen(open);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Training Information</DialogTitle>
            <DialogDescription>
              Add FAQ or business information to train your AI assistant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="add-title"
                placeholder="e.g., What are your store hours?"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-category">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger id="add-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">+ Add Custom Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.category === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="add-custom-category">
                    Custom Category Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="add-custom-category"
                    placeholder="Enter new category name"
                    value={formData.customCategory}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customCategory: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-custom-category-description">
                    Category Description
                  </Label>
                  <Textarea
                    id="add-custom-category-description"
                    placeholder="Enter category description (optional)"
                    value={formData.customCategoryDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customCategoryDescription: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="add-description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="add-description"
                placeholder="Enter detailed information..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={
                createMutation.isPending || createCategoryMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={
                createMutation.isPending || createCategoryMutation.isPending
              }
            >
              {createMutation.isPending || createCategoryMutation.isPending
                ? 'Adding...'
                : 'Add Information'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          if (!updateMutation.isPending && !createCategoryMutation.isPending) {
            setIsEditDialogOpen(open);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Training Information</DialogTitle>
            <DialogDescription>
              Update FAQ or business information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-title"
                placeholder="e.g., What are your store hours?"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger id="edit-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">+ Add Custom Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.category === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-custom-category">
                    Custom Category Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit-custom-category"
                    placeholder="Enter new category name"
                    value={formData.customCategory}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customCategory: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-custom-category-description">
                    Category Description
                  </Label>
                  <Textarea
                    id="edit-custom-category-description"
                    placeholder="Enter category description (optional)"
                    value={formData.customCategoryDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customCategoryDescription: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="edit-description"
                placeholder="Enter detailed information..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={
                updateMutation.isPending || createCategoryMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={
                updateMutation.isPending || createCategoryMutation.isPending
              }
            >
              {updateMutation.isPending || createCategoryMutation.isPending
                ? 'Updating...'
                : 'Update Information'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleteMutation.isPending) {
            setIsDeleteDialogOpen(open);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this training information. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
